import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI, quotationAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Package,
  FileText,
  Trophy,
  Clock,
  CheckCircle,
  PaperPlaneTilt,
  CircleNotch,
  Star,
  ChatCircle,
  CurrencyDollar,
  WarningCircle,
  Pencil,
  Trash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import DeliveryMap from '../components/DeliveryMap';
import MaterialImageUpload from '../components/MaterialImageUpload';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [subQuotations, setSubQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const response = await projectAPI.getById(projectId);
      setProject(response.data);
      
      // Backend already attaches sub_quotations via project_id lookup
      if (response.data.sub_quotations?.length > 0) {
        setSubQuotations(response.data.sub_quotations);
      }
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error('Error al cargar el proyecto');
    } finally {
      setLoading(false);
    }
  };

  const handleSendProject = async () => {
    setSending(true);
    try {
      await projectAPI.send(projectId);
      toast.success('¡Proyecto enviado a proveedores!');
      setShowSendDialog(false);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el proyecto');
    } finally {
      setSending(false);
    }
  };

  const handleSelectWinner = async (quotationId, proposalId) => {
    try {
      await quotationAPI.selectWinner(quotationId, proposalId);
      toast.success('¡Ganador seleccionado!');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al seleccionar ganador');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Borrador', className: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
      sent: { label: 'Enviado', className: 'bg-blue-100 text-blue-700 border-blue-300' },
      in_progress: { label: 'En Progreso', className: 'bg-amber-100 text-amber-700 border-amber-300' },
      completed: { label: 'Completado', className: 'bg-green-100 text-green-700 border-green-300' },
      cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-700 border-red-300' },
    };
    const config = statusMap[status] || { label: status, className: 'bg-zinc-100 text-zinc-700' };
    return <Badge variant="outline" className={`rounded-none ${config.className}`}>{config.label}</Badge>;
  };

  const getQuotationProgress = () => {
    if (!subQuotations.length) return 0;
    const withWinner = subQuotations.filter(q => q.winner_proposal_id).length;
    return (withWinner / subQuotations.length) * 100;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-20">
        <WarningCircle className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-zinc-900">Proyecto no encontrado</h2>
        <Link to="/projects" className="text-amber-600 hover:underline mt-2 inline-block">
          Volver a proyectos
        </Link>
      </div>
    );
  }

  const isOwner = user?.user_id === project.client_id;
  const isDraft = project.status === 'draft';

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/projects')}
          className="rounded-none w-fit"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-zinc-900">{project.name}</h1>
            {getStatusBadge(project.status)}
          </div>
          <p className="text-zinc-600 mt-1">
            Clave: <span className="font-mono">{project.key}</span>
          </p>
        </div>
        {isOwner && isDraft && (
          <div className="flex gap-2">
            <Link to={`/projects/${projectId}/edit`}>
              <Button variant="outline" className="rounded-none">
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </Link>
            <Button 
              onClick={() => setShowSendDialog(true)}
              className="btn-primary rounded-none"
              data-testid="send-project-btn"
            >
              <PaperPlaneTilt className="w-5 h-5 mr-2" />
              Enviar a Proveedores
            </Button>
          </div>
        )}
      </div>

      {/* Project Overview */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <Card className="lg:col-span-2 border-zinc-200 rounded-none">
          <CardHeader className="border-b border-zinc-200">
            <CardTitle>Información del Proyecto</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Fecha Límite</p>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-zinc-400" />
                  <span className="font-medium">
                    {new Date(project.deadline).toLocaleDateString('es-MX', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-zinc-500 mb-2">Ubicación de Entrega</p>
                {project.delivery_location?.latitude && project.delivery_location?.longitude ? (
                  <DeliveryMap
                    latitude={project.delivery_location.latitude}
                    longitude={project.delivery_location.longitude}
                    radius={project.delivery_location.radius || 5}
                    address={project.delivery_location.address || 'No especificada'}
                    readOnly={true}
                  />
                ) : (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-5 h-5 text-zinc-400 mt-0.5" />
                    <span className="font-medium">{project.delivery_location?.address || 'No especificada'}</span>
                  </div>
                )}
              </div>
            </div>
            
            {project.description && (
              <div className="mt-6">
                <p className="text-sm text-zinc-500 mb-1">Descripción</p>
                <p className="text-zinc-700">{project.description}</p>
              </div>
            )}

            <Separator className="my-6" />

            {/* Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-zinc-700">Progreso de Cotizaciones</p>
                <span className="text-sm text-zinc-500">
                  {subQuotations.filter(q => q.winner_proposal_id).length} / {subQuotations.length} completadas
                </span>
              </div>
              <Progress value={getQuotationProgress()} className="h-3 rounded-none" />
            </div>
          </CardContent>
        </Card>

        {/* Summary Card */}
        <Card className="border-zinc-200 rounded-none">
          <CardHeader className="border-b border-zinc-200">
            <CardTitle>Resumen</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">Materiales</span>
              <span className="font-bold text-zinc-900">{project.total_materials || project.all_materials?.length || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">Categorías</span>
              <span className="font-bold text-zinc-900">{project.total_categories || subQuotations.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">Sub-cotizaciones</span>
              <span className="font-bold text-zinc-900">{subQuotations.length}</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">Propuestas Recibidas</span>
              <span className="font-bold text-amber-600">
                {subQuotations.reduce((acc, q) => acc + (q.proposals_count || 0), 0)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sub-Quotations */}
      <Card className="border-zinc-200 rounded-none">
        <CardHeader className="border-b border-zinc-200">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Sub-cotizaciones por Categoría
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {subQuotations.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
              <p className="text-zinc-500">
                {isDraft 
                  ? 'Las sub-cotizaciones se crearán cuando envíes el proyecto' 
                  : 'No hay sub-cotizaciones'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200">
              {subQuotations.map((quotation) => (
                <div 
                  key={quotation.quotation_id} 
                  className="p-6"
                  data-testid={`subquotation-${quotation.quotation_id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge variant="outline" className="rounded-none bg-amber-50 text-amber-700 border-amber-200">
                          {quotation.category}
                        </Badge>
                        {quotation.winner_proposal_id && (
                          <Badge className="bg-green-500 rounded-none">
                            <Trophy className="w-3 h-3 mr-1" />
                            Ganador Seleccionado
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-zinc-600">
                        {quotation.materials?.length || 0} materiales en esta categoría
                      </p>
                      
                      {/* Materials Preview */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {quotation.materials?.slice(0, 3).map((m, i) => (
                          <span key={i} className="text-xs bg-zinc-100 px-2 py-1">
                            {m.name} ({m.quantity} {m.unit})
                          </span>
                        ))}
                        {quotation.materials?.length > 3 && (
                          <span className="text-xs text-zinc-500">
                            +{quotation.materials.length - 3} más
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-zinc-900">
                          {quotation.proposals_count || 0}
                        </p>
                        <p className="text-xs text-zinc-500">propuestas</p>
                      </div>
                      
                      {isOwner && (
                        <Link to={`/quotations/${quotation.quotation_id}`}>
                          <Button 
                            variant={quotation.winner_proposal_id ? "outline" : "default"}
                            className={quotation.winner_proposal_id ? "rounded-none" : "btn-primary rounded-none"}
                          >
                            {quotation.winner_proposal_id ? 'Ver Detalles' : 'Ver Propuestas'}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Winner Info */}
                  {quotation.winner_proposal_id && quotation.winner_info && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-700 font-medium">
                            Proveedor Ganador: {quotation.winner_info.provider_name || 'Proveedor'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Star className="w-4 h-4 text-amber-500" weight="fill" />
                            <span className="text-sm">{quotation.winner_info.rating?.toFixed(1) || '5.0'}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-700">
                            ${quotation.winner_info.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                          </p>
                          {quotation.chat_enabled && (
                            <Link to={`/chat/${quotation.quotation_id}`}>
                              <Button variant="ghost" size="sm" className="rounded-none mt-1">
                                <ChatCircle className="w-4 h-4 mr-1" />
                                Chat
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Materials List */}
      <Card className="border-zinc-200 rounded-none">
        <CardHeader className="border-b border-zinc-200">
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Lista de Materiales ({project.all_materials?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th className="w-16"></th>
                <th>Material</th>
                <th>Categoría</th>
                <th className="text-center">Cantidad</th>
                <th className="text-center">Unidad</th>
              </tr>
            </thead>
            <tbody>
              {project.all_materials?.map((material, index) => (
                <tr key={index}>
                  <td className="p-2">
                    <MaterialImageUpload imageUrl={material.image_url} readOnly={true} size="sm" />
                  </td>
                  <td className="font-medium">{material.name}</td>
                  <td>
                    <Badge variant="outline" className="rounded-none text-xs">
                      {material.category}
                    </Badge>
                  </td>
                  <td className="text-center">{material.quantity}</td>
                  <td className="text-center">{material.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Enviar Proyecto a Proveedores</DialogTitle>
            <DialogDescription>
              Se crearán {[...new Set(project.all_materials?.map(m => m.category) || [])].length} sub-cotizaciones 
              (una por categoría) y se notificará a los proveedores especializados.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-zinc-600">
              <strong>Importante:</strong> Una vez enviado, no podrás modificar los materiales del proyecto.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)} className="rounded-none">
              Cancelar
            </Button>
            <Button 
              onClick={handleSendProject}
              disabled={sending}
              className="btn-primary rounded-none"
            >
              {sending ? <CircleNotch className="w-5 h-5 animate-spin" /> : (
                <>
                  <PaperPlaneTilt className="w-5 h-5 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetailPage;
